<template>
  <div class="md:w-1/2 sm:w-full mx-auto px-4 py-10 text-gray-800">
    <header class="text-center">
      <h1 class="text-3xl sm:text-4xl font-extrabold tracking-tight">Privacy Policy for MyGraduationClip</h1>
      <p class="mt-3 text-base sm:text-lg">
        Last updated: <span class="font-medium">{{ lastUpdated || defaultUpdated }}</span>
      </p>
      <p class="mt-2 text-gray-600">
        At MyGraduationClip, we respect your privacy and are committed to protecting your personal data. This Privacy Policy explains what information we collect, how we use it, who we share it with, and your rights under applicable data protection laws, including the UK GDPR, the EU GDPR, and, where relevant, Australian privacy law. We will update this Privacy Policy on this page, showing the month and year of the latest revision.
      </p>
    </header>

    <!-- 1. Who We Are -->
    <section class="mt-10">
      <h2 class="text-2xl font-bold">1. Who We Are</h2>
      <p class="mt-3">MyGraduationClip ("we", "our", "us") provides digital graduation services, including personalised graduation video clips and Snapshot photo access.</p>
      <div class="mt-4 space-y-3">
        <p><span class="font-semibold">Data Controller:</span> We act as a data controller when you give us data directly — for example, registration details, survey responses (both required and optional), and account preferences. In these cases, we determine what data is needed to provide the GraduationClip service.</p>
        <p><span class="font-semibold">Data Processor or service provider:</span> When your university, education provider, photography partner, or event partner provides us with personal data or event media (e.g. student lists, degree details, graduation footage, graduation photos, or where surveys are mandated by the university), we process that data to provide the relevant service and under the applicable contractual instructions.</p>
      </div>
      <div class="mt-4 bg-gray-50 rounded-2xl p-4">
        <p>📧 Contact us: <a v-if="contactEmail" :href="`mailto:${contactEmail}`" class="text-blue-700 underline">{{ contactEmail }}</a></p>
        <p v-if="postalAddress" class="mt-1">📍 <span>{{ postalAddress }}</span><span></span></p>
        <p class="mt-3 text-sm text-gray-600">In the UK, you can also contact the ICO (Information Commissioner’s Office). In the EU, you may contact your local supervisory authority.</p>
      </div>
    </section>

    <!-- 2. Information We Collect -->
    <section class="mt-10">
      <h2 class="text-2xl font-bold">2. Information We Collect</h2>
      <div class="mt-4">
        <h3 class="font-semibold">From Your University, Education Provider, Photography Partner, or Event Partner:</h3>
        <ul class="list-disc list-inside mt-2 space-y-1">
          <li>Student name, email address, degree title, graduation date/time, student ID, graduation footage, and, where Snapshot is used, graduation photos for the relevant ceremony.</li>
        </ul>
      </div>
      <div class="mt-6">
        <h3 class="font-semibold">Directly From You (controller role):</h3>
        <ul class="list-disc list-inside mt-2 space-y-1">
          <li>Account details (name, email, ceremony details).</li>
          <li>Survey responses (required where necessary for the service, or optional where you choose to provide them).</li>
          <li>Account preferences or other consent-based data.</li>
          <li>Where you use Snapshot, a selfie or photo you upload for facial matching and photo access. For one-off Snapshot searches, the uploaded selfie or photo is used to perform the match and is not stored by MyGraduationClip after that search.</li>
        </ul>
      </div>
      <div class="mt-6">
        <h3 class="font-semibold">Automatically Collected:</h3>
        <ul class="list-disc list-inside mt-2 space-y-1">
          <li>Usage data (IP address, browser type, device details, interaction logs).</li>
        </ul>
        <p class="mt-2">We do not collect precise location data. We do not sell advertising.</p>
      </div>
    </section>

    <!-- 3. How We Use Your Information -->
    <section class="mt-10">
      <h2 class="text-2xl font-bold">3. How We Use Your Information</h2>
      <ul class="list-disc list-inside mt-3 space-y-1">
        <li>To deliver GraduationClips and related services.</li>
        <li>To provide Snapshot photo access, including photo matching, access control, digital download, support, security, and related operational services.</li>
        <li>To distribute graduation videos and notifications on behalf of your university.</li>
        <li>To process payments (via Stripe).</li>
        <li>To send event photo/order reminders where you have entered your contact details while viewing Snapshot photos and have not opted out.</li>
        <li>To manage student participation in graduation events.</li>
        <li>To provide aggregated analytics to universities.</li>
        <li>To analyse service usage and improve performance.</li>
        <li>To ensure platform security and stability.</li>
        <li>To comply with legal, regulatory, or contractual obligations.</li>
      </ul>
    </section>

    <!-- 4. Legal Basis for Processing -->
    <section class="mt-10">
      <h2 class="text-2xl font-bold">4. Legal Basis for Processing</h2>
      <ul class="list-disc list-inside mt-3 space-y-1">
        <li><span class="font-semibold">Contractual necessity</span> – to provide the GraduationClip service you sign up for, including required survey questions where needed for service delivery.</li>
        <li><span class="font-semibold">Legitimate interests</span> – to ensure security, prevent fraud, analyse performance, and improve our platform (balanced against your rights).</li>
        <li><span class="font-semibold">Soft opt-in for event photo reminders</span> – where permitted, to send limited reminders about Snapshot photos you viewed or started to order, with an opt-out at collection and unsubscribe in every reminder.</li>
        <li><span class="font-semibold">Consent</span> – for optional survey responses or additional preferences not strictly required.</li>
        <li><span class="font-semibold">Explicit consent</span> – where Snapshot uses facial matching that may involve facial biometric information or other sensitive information.</li>
        <li><span class="font-semibold">Legal obligation</span> – to comply with financial, tax, or regulatory requirements.</li>
        <li><span class="font-semibold">On behalf of universities, education providers, or event partners</span> – when processing student data or event media supplied by them, we act under the relevant contractual instructions.</li>
      </ul>
    </section>

    <!-- 5. Snapshot Photo Access and Facial Matching -->
    <section class="mt-10">
      <h2 class="text-2xl font-bold">5. Snapshot Photo Access and Facial Matching</h2>
      <div class="mt-3 space-y-3">
        <p>For some graduation events, MyGraduationClip provides a photo access service called Snapshot. Snapshot allows graduates to find and access graduation photos taken at their ceremony.</p>
        <p>The graduation photos used in Snapshot may be taken by MyGraduationClip or by a third-party photography or event partner. Where a third-party partner takes the photos, that partner may provide or automatically upload the ceremony photos to the MyGraduationClip platform so they can be made available through Snapshot.</p>
        <p>Where Snapshot uses facial matching, we may process facial measurements or other biometric information derived from ceremony photos and from a selfie or photo uploaded by you. Biometric information may be treated as sensitive information or special category data under applicable privacy laws.</p>
        <p>Snapshot facial matching is used only to identify and show photos matched to you, control access to those photos, provide digital downloads, support users, maintain service security, and operate the Snapshot service for the relevant event. We do not use uploaded selfies, facial measurements, or biometric information for advertising.</p>
        <p>If facial matching is required for a particular event, we will ask for your explicit consent before carrying out the match. If you do not provide that consent, you may not be able to access your graduation photos through Snapshot.</p>
        <p>For one-off Snapshot searches, the selfie or photo you upload is used to perform the match and is not stored by MyGraduationClip after that search. Facial matching may be performed using our cloud service providers, including AWS facial recognition services. MyGraduationClip does not sell, rent, or trade uploaded selfies, facial measurements, or biometric information.</p>
        <p>We may retain limited operational records, such as consent records, order/download records, support records, security logs, and technical audit logs where needed to provide the service, support users, maintain security, meet contractual obligations, or comply with legal requirements.</p>
        <p>Where Snapshot reminders are enabled for an event, we may retain minimal contact details and reminder status for a short period so we can remind you about photos you viewed or started to order. You can opt out when entering your details and unsubscribe from reminder emails at any time.</p>
      </div>
    </section>

    <!-- 6. Sharing of Data -->
    <section class="mt-10">
      <h2 class="text-2xl font-bold">6. Sharing of Data</h2>
      <p class="mt-3">We may share your data with:</p>
      <ul class="list-disc list-inside mt-2 space-y-1">
        <li><span class="font-semibold">Your University</span> – for graduation management, required surveys, analytics, and service reporting.</li>
        <li><span class="font-semibold">Education providers, photography partners, and event partners</span> – where needed to provide Snapshot, photography, photo access, support, reporting, or event services.</li>
        <li><span class="font-semibold">Service Providers:</span>
          <ul class="list-disc list-inside ml-6 mt-1 space-y-1">
            <li>Stripe (payment processing).</li>
            <li>Amazon Web Services (AWS) (hosting, storage, encryption, serverless functions, and, where Snapshot facial matching is used, facial matching services).</li>
          </ul>
        </li>
      </ul>
      <p class="mt-2">All providers operate under strict data protection agreements.</p>
    </section>

    <!-- 7. How We Store and Protect Your Data -->
    <section class="mt-10">
      <h2 class="text-2xl font-bold">7. How We Store and Protect Your Data</h2>
      <ul class="list-disc list-inside mt-3 space-y-1">
        <li>Data is hosted on AWS.</li>
        <li>All data is encrypted in transit (TLS) and at rest (AES-256).</li>
        <li>Daily encrypted backups and vulnerability scans.</li>
        <li>Regular penetration tests and security reviews.</li>
        <li>ISO 27001 suppliers, PCI compliance, anomaly detection, firewalls, and access controls.</li>
      </ul>
    </section>

    <!-- 8. International Data Transfers -->
    <section class="mt-10">
      <h2 class="text-2xl font-bold">8. International Data Transfers</h2>
      <p class="mt-3">If your data is transferred outside the UK, EEA, Australia, or your local jurisdiction, we use appropriate safeguards required by applicable law, such as contractual protections, Standard Contractual Clauses (where relevant), or adequacy decisions.</p>
    </section>

    <!-- 9. Data Retention -->
    <section class="mt-10">
      <h2 class="text-2xl font-bold">9. Data Retention</h2>
      <ul class="list-disc list-inside mt-3 space-y-1">
        <li><span class="font-semibold">Account data:</span> retained while your account is active.</li>
        <li><span class="font-semibold">University, education provider, photography partner, or event partner data:</span> retained for the period agreed in the relevant contract or as otherwise required to provide the service.</li>
        <li><span class="font-semibold">Directly provided data (including surveys):</span> we keep your data for 5 years unless specified otherwise in a contract with your university, or until you withdraw consent (for optional data).</li>
        <li><span class="font-semibold">Snapshot one-off search selfies/photos:</span> used to perform the facial matching search and not stored by MyGraduationClip after that search.</li>
        <li><span class="font-semibold">Snapshot photos, orders, downloads, support records, consent records, and logs:</span> retained only for as long as needed to provide the service, support users, maintain security, meet contractual obligations, and comply with legal requirements. Generated download files may be retained for a shorter period.</li>
        <li><span class="font-semibold">Payment data:</span> retained as required by financial and tax laws.</li>
      </ul>
    </section>

    <!-- 10. Your Rights -->
    <section class="mt-10">
      <h2 class="text-2xl font-bold">10. Your Rights</h2>
      <p class="mt-3">Depending on where you are and which privacy laws apply, you may have rights to:</p>
      <ul class="list-disc list-inside mt-2 space-y-1">
        <li>Access a copy of your personal data.</li>
        <li>Correct inaccurate information.</li>
        <li>Request deletion of your data ("Right to be Forgotten"), subject to legal or contractual obligations.</li>
        <li>Restrict or object to processing.</li>
        <li>Request data portability.</li>
        <li>Withdraw consent at any time (where consent is the basis).</li>
        <li>Lodge a complaint with a supervisory authority.</li>
      </ul>
      <p class="mt-2">To exercise these rights, contact us at <a v-if="contactEmail" :href="`mailto:${contactEmail}`" class="text-blue-700 underline">{{ contactEmail }}</a><span v-else>[Insert contact email]</span>. For Snapshot events involving a university, education provider, photography partner, or event partner, you may also need to contact that organisation about how they handle your information.</p>
    </section>

    <!-- 11. Cookies and Tracking -->
    <section class="mt-10">
      <h2 class="text-2xl font-bold">11. Cookies and Tracking</h2>
      <ul class="list-disc list-inside mt-3 space-y-1">
        <li><span class="font-semibold">Essential cookies</span> – required for login and security.</li>
        <li><span class="font-semibold">Analytics and diagnostic data</span> – handled through our internal tooling to understand service usage and improve performance. We do not use Google Analytics.</li>
      </ul>
    </section>

    <!-- 12. Children’s Privacy -->
    <section class="mt-10">
      <h2 class="text-2xl font-bold">12. Children’s Privacy</h2>
      <p class="mt-3">Our services are intended only for university students. We do not knowingly collect data from anyone under 16.</p>
    </section>

    <!-- 13. Changes to This Policy -->
    <section class="mt-10">
      <h2 class="text-2xl font-bold">13. Changes to This Policy</h2>
      <p class="mt-3">We may update this Privacy Policy from time to time. The latest version will always be available on this page, with the month and year of revision shown at the top.</p>
    </section>
  </div>
</template>

<script>
export default {
  name: "PrivacyPolicy",
  props: {
    lastUpdated: { type: String, default: "May 2026" },
    contactEmail: { type: String, default: "privacy@mygraduationclip.com" },
    postalAddress: { type: String, default: "" }
  },
  computed: {
    defaultUpdated() {
      // Fallback to a sensible default (e.g., current month and year)
      const date = new Date();
      const month = date.toLocaleString(undefined, { month: "long" });
      const year = date.getFullYear();
      return `${month} ${year}`;
    }
  }
};
</script>

<style scoped>
/* Optional: improve readability */
section + section { border-top: 1px solid #e5e7eb; padding-top: 2rem; }
</style>
